import api from "@nitro-ape/shared/platform/api/api";
import {
  ACCESS_TOKEN_COOKIE,
  REFRESH_TOKEN_COOKIE,
} from "@nitro-ape/shared/lib/constants";
import { getEnv } from "@/lib/env";
import { AuthMode } from "@nitro-ape/shared/models/enums/auth-mode";
import {
  TokensResponse,
  TokensResponseSchema,
} from "@nitro-ape/shared/models/tokens-response";
import { i18n } from "@lingui/core";
import { useIdentityToken, usePrivy } from "@privy-io/react-auth";
import { useRouter } from "@tanstack/react-router";
import Cookies from "js-cookie";
import { useCallback, useState } from "react";
import { toast } from "sonner";

export const usePrivyAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { getAccessToken, authenticated } = usePrivy();
  const { identityToken } = useIdentityToken();

  const handlePrivyLogin = useCallback(
    async (refCode?: string) => {
      if (!authenticated) {
        toast.error(
          i18n.t({
            id: "hooks.privyAuth.notAuthenticated",
            message: "Not authenticated with Privy",
            comment:
              "Toast shown when trying to exchange token but not authenticated with Privy.",
          }),
        );
        return;
      }

      try {
        setIsLoading(true);

        const privyToken = await getAccessToken();

        if (!privyToken || !identityToken) {
          throw new Error("Failed to get Privy access token");
        }

        const platform = getEnv.VITE_PLATFORM;

        console.log("sending request to backend");
        const backendResponse = await api.post<TokensResponse>("/auth/privy", {
          privyIdToken: identityToken,
          ...(refCode && { refCode }),
          ...(platform && { platform }),
        });

        console.log("received response from backend");

        const parsedData = TokensResponseSchema.parse(backendResponse.data);

        const authMode = (getEnv.VITE_AUTH_MODE || AuthMode.header) as AuthMode;

        if (authMode === AuthMode.header) {
          const { access_token, refresh_token } = parsedData;
          Cookies.set(ACCESS_TOKEN_COOKIE, access_token ?? "");
          Cookies.set(REFRESH_TOKEN_COOKIE, refresh_token ?? "");
        }

        router.navigate({ to: "/explore" });
      } catch (error) {
        console.error("Privy authentication failed:", error);
        toast.error(
          i18n.t({
            id: "hooks.privyAuth.exchangeFailure",
            message: "Authentication failed, please try again",
            comment:
              "Toast shown when exchanging Privy token for backend token fails.",
          }),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [authenticated, getAccessToken, identityToken, router],
  );

  return {
    handlePrivyLogin,
    isLoading,
  };
};
